// src/components/modules/views/dashboard/BusinessInsights/VisionDelNegocio/ExecutiveSummaryContext.js

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { BusinessAPI } from '../../../../../../../api/business.api';
import GlobalContext from '../../../../../../../context/global-context';

const BusinessInsightContext = createContext(null);

const createInitialData = () => ({
  summaryDataClientes: {
    monto: 0,
    saldo: 0,
    pagado: 0,
    cobrado: 0,
    totalNC: 0,
    porCobrar: 0,
    totalConNC: 0,
    totalFacturadoNeto: 0
  },
  agingChartData: [],
  monthlySummaryFromCodes: {
    ingresos: {},
    gastos: {},
    total: {},
    uniquePeriods: []
  },
  estadoResultadoResumen: {
    periods: [],
    rows: []
  },
  erIngresosMesActual: 0,
  erGastosMesActual: 0,
  erIngresosUlt3: 0,
  erGastosUlt3: 0,
  erIngresosAnual: 0,
  erGastosAnual: 0,
  porcentajeValor: 0,
  totalSaldoAcumulado: 0,
  monthlyChartData: [],
  weeklyChartData: [],
  receivablesDueChartData: [],
  vencidoPorCobrar: 0,
  vencidoPorPagar: 0,
  totales: {
    totalAbono: 0,
    totalCargo: 0,
    diferencia: 0
  },
  promedioMensual: 0,
  runAway: 0,
  resultado: {
    montoTotalGeneral: 0,
    cuentasAgrupadas: {}
  },
  erByMonthFromCodes: {},
  weeklyERSummary: {},
  realVsBudget: [],
  topClients: [],
  topProviders: []
})

export const BusinessInsightProvider = ({ children }) => {
  const { currentBusiness } = useContext(GlobalContext);

  const [data, setData] = useState(createInitialData)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasLoadedData, setHasLoadedData] = useState(false)
  const businessAPI = useMemo(() => new BusinessAPI(), [])
  const requestIdRef = useRef(0)

  const applyData = useCallback(apiData => {
    if (!apiData || typeof apiData !== 'object') {
      throw new Error('No hay datos disponibles para Visión del negocio')
    }

    setData({
      ...createInitialData(),
      ...apiData,
      weeklyERSummary: apiData.weeklyErSummary || apiData.weeklyERSummary || {}
    })
    setHasLoadedData(true)
    setError(null)
  }, [])

 const fetchData = useCallback(async () => {
    const businessId = currentBusiness?.id
    if (!businessId) return false

    const requestId = ++requestIdRef.current
    setError(null)
    setLoading(true)
    try {
      const response = await businessAPI.getLatestVisionDelNegocio({
        businessId
      })

      if (requestId !== requestIdRef.current) return false
      applyData(response.data)
      return true
    } catch (error) {
      if (requestId === requestIdRef.current) setError(error)
      return false
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [applyData, businessAPI, currentBusiness?.id])

  useEffect(() => {
    requestIdRef.current += 1
    setData(createInitialData())
    setHasLoadedData(false)
    setError(null)
    setLoading(false)
    if (!currentBusiness?.id) return
    fetchData()
  }, [currentBusiness?.id, fetchData])

  useEffect(() => {
    const handleDashboardRegenerated = event => {
      const eventBusinessId = event.detail?.businessId
      if (eventBusinessId && Number(eventBusinessId) !== Number(currentBusiness?.id)) return

      if (event.detail?.data) {
        requestIdRef.current += 1
        setLoading(false)
        applyData(event.detail.data)
        return
      }

      fetchData()
    }
    
    window.addEventListener('dashboardRegenerated', handleDashboardRegenerated)

    return () => {
      window.removeEventListener('dashboardRegenerated', handleDashboardRegenerated)
    }
  }, [applyData, currentBusiness?.id, fetchData])

  // ========== 4) value del Provider ==========
  const value = {
    data,
    loading,
    error,
    hasLoadedData,
    refetch: fetchData
  };

  return (
    <BusinessInsightContext.Provider value={value}>
      {children}
    </BusinessInsightContext.Provider>
  );
};

export const useBusinessInsightContext = () => {
  const context = useContext(BusinessInsightContext);
  if (!context) {
    throw new Error(
      'useBusinessInsightContext must be used within an BusinessInsightProvider'
    );
  }
  return context;
};
